/**
 * TypeScript interfaces for the Chat System
 */

import { ChatConversation } from '@/services/chat';

// Re-export main types from services for convenience
export type {
  ChatMessage,
  ChatConversation,
  SendMessageRequest,
} from '@/services/chat';

// Component prop types
export interface ChatSystemConfig {
  maxConversations?: number;
  typingSpeed?: number;
  typingInterval?: number;
  autoFocus?: boolean;
  maxMessageLength?: number;
  drawerWidth?: number | string;
  drawerPlacement?: 'left' | 'right' | 'top' | 'bottom';
  buttonPosition?: {
    bottom?: number;
    right?: number;
    left?: number;
    top?: number;
  };
  buttonSize?: 'small' | 'middle' | 'large';
}

// Streaming related types
export interface StreamingState {
  isActive: boolean;
  conversationId?: number;
  messageId?: string;
  controller?: AbortController;
}

// Chat events
export interface ChatEvents {
  onConversationStart?: (conversationId: number) => void;
  onConversationEnd?: (conversationId: number) => void;
  onMessageSent?: (conversationId: number, message: string) => void;
  onMessageReceived?: (conversationId: number, message: string) => void;
  onError?: (error: Error) => void;
  onStreamingStart?: (conversationId: number) => void;
  onStreamingEnd?: (conversationId: number) => void;
}

// Chat analytics/metrics
export interface ChatMetrics {
  totalConversations: number;
  totalMessages: number;
  averageResponseTime: number;
  activeConversations: number;
}

// Message metadata
export interface MessageMetadata {
  responseTime?: number;
  tokenCount?: number;
  modelUsed?: string;
  confidence?: number;
}

// Extended message type with metadata
export interface ExtendedChatMessage {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  isStreaming?: boolean;
  metadata?: MessageMetadata;
  reactions?: string[];
  edited?: boolean;
  editedAt?: Date;
}

// Conversation settings
export interface ConversationSettings {
  temperature?: number;
  maxTokens?: number;
  model?: string;
  systemPrompt?: string;
  contextLength?: number;
}

// Chat theme customization
export interface ChatTheme {
  primaryColor?: string;
  userMessageColor?: string;
  assistantMessageColor?: string;
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
  borderRadius?: number;
  fontSize?: number;
  fontFamily?: string;
}

// Storage interface for persisting conversations
export interface ChatStorage {
  saveConversation: (conversation: ChatConversation) => Promise<void>;
  loadConversations: () => Promise<ChatConversation[]>;
  deleteConversation: (conversationId: number) => Promise<void>;
  clearAllConversations: () => Promise<void>;
}

// Hook return types
export interface UseChatReturn {
  conversations: ChatConversation[];
  activeConversation?: ChatConversation;
  isStreaming: boolean;
  sendMessage: (message: string) => Promise<void>;
  createConversation: () => Promise<number>;
  deleteConversation: (id: number) => void;
  setActiveConversation: (id: number) => void;  
  stopStreaming: () => void;
  metrics: ChatMetrics;
}

// Error types
export enum ChatErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  API_ERROR = 'API_ERROR',
  STREAMING_ERROR = 'STREAMING_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  RATE_LIMIT_ERROR = 'RATE_LIMIT_ERROR',
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
}

export interface ChatError extends Error {
  type: ChatErrorType;
  code?: string;
  details?: any;
  conversationId?: number;
  messageId?: string;
}

// Utility type for component refs
export interface ChatSystemRef {
  openChat: () => void;
  closeChat: () => void;
  sendMessage: (message: string) => void;
  createNewConversation: () => void;
  getActiveConversation: () => ChatConversation | undefined;
  getAllConversations: () => ChatConversation[];
}

// Default configurations
export const DEFAULT_CHAT_CONFIG: Required<ChatSystemConfig> = {
  maxConversations: 5,
  typingSpeed: 2,
  typingInterval: 30,
  autoFocus: true,
  maxMessageLength: 4000,
  drawerWidth: 480,
  drawerPlacement: 'right',
  buttonPosition: { bottom: 24, right: 24 },
  buttonSize: 'large',
};

export const DEFAULT_CHAT_THEME: Required<ChatTheme> = {
  primaryColor: '#1890ff',
  userMessageColor: '#1890ff',
  assistantMessageColor: '#f5f5f5',
  backgroundColor: '#ffffff',
  textColor: '#262626',
  borderColor: '#f0f0f0',
  borderRadius: 16,
  fontSize: 14,
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};
